import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, KeyRound, ShieldAlert, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { api } from '../api'
import { ErrorState, LoadingState, PageHeader, Panel, StatusBadge } from '../components/ui'

function normalizedCode(value: string): string {
  return value.trim().toUpperCase().replace(/[\s-]+/gu, '')
}

export function CliAuthorizationPage() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useSearchParams()
  const requestedCode = search.get('user_code') ?? ''
  const [draftCode, setDraftCode] = useState(requestedCode)
  const code = normalizedCode(requestedCode)
  const authorization = useQuery({
    queryKey: ['device-authorization', code],
    queryFn: () => api.deviceAuthorization(code),
    enabled: code.length === 8,
    retry: false,
  })
  const approve = useMutation({
    mutationFn: () => api.approveDeviceAuthorization(code),
    onSuccess: (result) =>
      queryClient.setQueryData(['device-authorization', code], result),
  })
  const deny = useMutation({
    mutationFn: () => api.denyDeviceAuthorization(code),
    onSuccess: (result) =>
      queryClient.setQueryData(['device-authorization', code], result),
  })

  const submitCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const normalized = normalizedCode(draftCode)
    if (normalized.length === 8) setSearch({ user_code: normalized })
  }

  const state = authorization.data?.authorization.state
  const pending = state === 'pending'
  const mutationError = approve.error ?? deny.error

  return (
    <>
      <PageHeader
        title={t('Authorize Computinger CLI')}
        description={t(
          'Review this request before granting terminal access to your Developer Center account.'
        )}
      />
      {code.length !== 8 ? (
        <Panel>
          <form className='cli-code-form' onSubmit={submitCode}>
            <label htmlFor='cli-code'>{t('Authorization code')}</label>
            <p>{t('Enter the code shown in your terminal.')}</p>
            <div className='horizontal-form'>
              <input
                id='cli-code'
                autoComplete='one-time-code'
                inputMode='text'
                maxLength={9}
                placeholder='ABCD-EFGH'
                value={draftCode}
                onChange={(event) => setDraftCode(event.target.value)}
              />
              <button
                className='button button-primary'
                type='submit'
                disabled={normalizedCode(draftCode).length !== 8}
              >
                {t('Continue')}
              </button>
            </div>
          </form>
        </Panel>
      ) : authorization.isPending ? (
        <LoadingState />
      ) : authorization.isError ? (
        <ErrorState error={authorization.error} retry={() => void authorization.refetch()} />
      ) : (
        <Panel className='cli-authorization-card'>
          <div className='cli-authorization-mark' aria-hidden='true'>
            {state === 'approved' || state === 'consumed' ? (
              <CheckCircle2 size={28} />
            ) : state === 'denied' ? (
              <XCircle size={28} />
            ) : (
              <KeyRound size={28} />
            )}
          </div>
          <div>
            <div className='panel-header'>
              <div>
                <h2>{t('CLI authorization request')}</h2>
                <p className='cli-code-value'>{requestedCode || code}</p>
              </div>
              <StatusBadge state={state ?? 'expired'} />
            </div>
            <dl className='detail-list'>
              <div>
                <dt>{t('Application')}</dt>
                <dd>{authorization.data.authorization.clientName}</dd>
              </div>
              <div>
                <dt>{t('Version')}</dt>
                <dd>{authorization.data.authorization.clientVersion}</dd>
              </div>
              <div>
                <dt>{t('Expires at')}</dt>
                <dd>
                  {new Date(authorization.data.authorization.expiresAt).toLocaleString(
                    i18n.resolvedLanguage ?? i18n.language
                  )}
                </dd>
              </div>
            </dl>
            {pending ? (
              <>
                <div className='security-note'>
                  <ShieldAlert size={18} />
                  <p>{t('Only approve a code that you initiated in the official Computinger CLI.')}</p>
                </div>
                {mutationError ? <ErrorState error={mutationError} /> : null}
                <div className='form-actions'>
                  <button
                    className='button button-secondary'
                    type='button'
                    disabled={approve.isPending || deny.isPending}
                    onClick={() => deny.mutate()}
                  >
                    {t('Deny')}
                  </button>
                  <button
                    className='button button-primary'
                    type='button'
                    disabled={approve.isPending || deny.isPending}
                    onClick={() => approve.mutate()}
                  >
                    {t('Approve CLI access')}
                  </button>
                </div>
              </>
            ) : (
              <p className='completion-message'>
                {state === 'denied'
                  ? t('This terminal will not receive an access token.')
                  : t('You can close this page and return to the terminal.')}
              </p>
            )}
          </div>
        </Panel>
      )}
    </>
  )
}
